import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Nur Deutsch (Default + Fallback) wird statisch ins Entry-Bundle gebacken.
// Englisch und Thai werden bei Bedarf per dynamischem Import nachgeladen — das
// nimmt ~1,7 MB JSON (EN ~660 KB + TH ~1,08 MB) aus dem Entry-Chunk und
// beschleunigt den First Paint nach dem Login deutlich.
import de from './locales/de.json';
import { webPosTranslations } from '../webpos/i18n';
import { zahlartenTranslations } from '../components/zahlarten/i18n';
import { incomingInvoicesTranslations } from '../pages/incoming-invoices/i18n';
import { shopPflichtmailsTranslations } from '../components/settings/shopPflichtmails/i18n';
import { mailVerlaufTranslations } from '../components/customers/mailVerlaufI18n';

export const languages = [
  { code: 'de', name: 'Deutsch', flag: '🇩🇪', nativeName: 'Deutsch' },
  { code: 'en', name: 'English', flag: '🇬🇧', nativeName: 'English' },
  { code: 'th', name: 'Thai', flag: '🇹🇭', nativeName: 'ไทย' },
] as const;

export type LanguageCode = typeof languages[number]['code'];

// Lazy-Loader pro Sprache (DE ist immer statisch vorhanden).
const lazyLocaleLoaders: Record<string, () => Promise<{ default: Record<string, unknown> }>> = {
  en: () => import('./locales/en.json'),
  th: () => import('./locales/th.json'),
};

const webPos = webPosTranslations as Record<string, Record<string, unknown>>;
const zahlarten = zahlartenTranslations as Record<string, Record<string, unknown>>;
const incomingInvoicesZahlart = incomingInvoicesTranslations as Record<string, Record<string, unknown>>;
const shopPflichtmails = shopPflichtmailsTranslations as Record<string, Record<string, unknown>>;
const mailVerlauf = mailVerlaufTranslations as Record<string, Record<string, unknown>>;

/**
 * Stellt sicher, dass das Sprachpaket der Zielsprache geladen ist.
 * DE ist immer da; EN/TH werden einmalig per dynamischem Import nachgezogen.
 */
async function ensureLanguageLoaded(lng: string | undefined): Promise<void> {
  const base = (lng || 'de').split('-')[0];
  if (base === 'de' || i18n.hasResourceBundle(base, 'translation')) return;
  const loader = lazyLocaleLoaders[base];
  if (!loader) return;
  try {
    const mod = await loader();
    i18n.addResourceBundle(
      base,
      'translation',
      { ...mod.default, ...(webPos[base] || {}), ...(zahlarten[base] || {}), ...(incomingInvoicesZahlart[base] || {}), ...(shopPflichtmails[base] || {}), ...(mailVerlauf[base] || {}) },
      true,
      true,
    );
  } catch (err) {
    // Bei Ladefehler bleibt der DE-Fallback aktiv — App funktioniert weiter.
    console.warn('[i18n] Sprachpaket konnte nicht geladen werden:', base, err);
  }
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      de: { translation: { ...de, ...webPosTranslations.de, ...zahlartenTranslations.de, ...incomingInvoicesTranslations.de, ...shopPflichtmailsTranslations.de, ...mailVerlaufTranslations.de } },
    },
    fallbackLng: 'de',
    supportedLngs: ['de', 'en', 'th'],
    // Region-Codes des Browsers (z. B. "de-DE", "en-US") auf die Basissprache
    // abbilden. Ohne dies verwirft i18next "de-DE" als nicht unterstützt und fällt
    // auf den htmlTag (<html lang="en">) zurück — neue deutsche Besucher sahen so
    // fälschlich Englisch (u. a. auf der Registrierungsseite).
    nonExplicitSupportedLngs: true,
    // Regionssuffix verwerfen, damit i18n.language sauber "de"/"en"/"th" ist
    // (sonst matcht der Sprach-Umschalter die Flagge nicht und Lookups streuen).
    load: 'languageOnly',

    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'bispicy-language',
    },

    interpolation: {
      escapeValue: false,
    },

    react: {
      useSuspense: false,
    },
  });

// Bei jedem Sprachwechsel das Zielpaket nachladen und danach EINMAL erneut
// auf die Sprache schalten, damit react-i18next mit den neuen Strings rendert.
// Re-Entrancy-Guard verhindert eine Endlosschleife (changeLanguage feuert das
// Event erneut, der zweite Durchlauf findet das Bundle bereits vor).
let loadingLanguage = false;
i18n.on('languageChanged', (lng) => {
  const base = (lng || 'de').split('-')[0];
  if (base === 'de' || i18n.hasResourceBundle(base, 'translation') || loadingLanguage) return;
  loadingLanguage = true;
  void ensureLanguageLoaded(lng).finally(() => {
    loadingLanguage = false;
    i18n.changeLanguage(lng);
  });
});

// Startsprache: Hat die Spracherkennung beim Init bereits EN/TH gewählt (z. B.
// wiederkehrender Nutzer mit gespeicherter Präferenz), das Paket sofort nachladen
// und neu anwenden.
void (async () => {
  const base = (i18n.language || 'de').split('-')[0];
  if (base !== 'de' && !i18n.hasResourceBundle(base, 'translation')) {
    await ensureLanguageLoaded(i18n.language);
    i18n.changeLanguage(i18n.language);
  }
})();

export default i18n;
